import { AppFilterType, filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import {
  EmptyStateBase,
  Page,
  PaginationBlock,
  useModals,
  Button,
  CoreFilterType,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Search,
  Stack,
  useDebouncedSearchParam,
} from "@skailer-com/uikit";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { useGoalsCustomFilters } from "@/pages/talent/goals/widgets/GoalsCustomFilters.tsx";
import { GoalsTable } from "@/pages/talent/goals/components/GoalsTable.tsx";
import { useGoalsTableHook } from "@/pages/talent/goals/hooks/use-goals-table.hook.ts";
import { useGoalsSearch } from "@/queries/goals.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiGoalOwnerType,
  ApiGoalSearchFilter,
  GoalConfidenceLevelItems,
} from "@/types/goals.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { GoalPaneLayout } from "@/pages/talent/goals/components/GoalPaneLayout.tsx";
import { useGoalSidePanel } from "@/pages/talent/goals/hooks/use-goal-side-panel.hook.ts";

export function InitiativesGoalsPage() {
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();
  const modalsService = useModals();
  const [, setSearchParams] = useSearchParams();
  const { openGoalPanel } = useGoalSidePanel();
  const {
    value: searchTerm,
    setValue: setSearchTerm,
    clear: clearSearch,
  } = useDebouncedSearchParam({
    paramName: "search",
    delay: 300,
  });

  const library = filtersLibrary();
  const { filterAdditions, renderFilters } = useGoalsCustomFilters();

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiGoalSearchFilter>({
    filters: [
      library.page_number,
      library.text,
      {
        name: "confidence_level",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.multiselect,
        label: t("goals:confidence_level"),
        options: GoalConfidenceLevelItems(),
      },
      {
        name: "owner_type",
        type: AppFilterType.goalOwner,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.custom,
        label: t("goals:owner_type"),
        options: [
          { id: ApiGoalOwnerType.department, title: t("core:department") },
          { id: ApiGoalOwnerType.team, title: t("core:team") },
          { id: ApiGoalOwnerType.employee, title: t("core:employee") },
        ],
      },
      {
        name: "owner_id",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.numberArray,
        renderType: FilterRenderType.none,
        hideInFilter: true,
      },
      {
        name: "assignee_ids",
        type: AppFilterType.employee,
        valueType: FilterValueType.numberArray,
        renderType: FilterRenderType.custom,
        label: t("goals:assignees"),
      },
    ],
    source: "goals",
    renderFilterButton: ({ onClick, count }) => (
      <Button
        variant={count > 0 ? "primary" : "secondary"}
        leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
        rightIcon={count > 0 ? <NotificationCount count={count} size="sm" /> : undefined}
        onClick={onClick}
      >
        {t("common:filter")}
      </Button>
    ),
  });

  const filterWithCustom = useMemo<ApiGoalSearchFilter>(() => {
    const hasSearchText = (filter?.text ?? "").length > 0;

    return {
      ...filter,
      ...filterAdditions,
      only_top_level: !hasSearchText,
      only_initiatives: true,
      owner_type: filter.owner_type?.length
        ? filter.owner_type
        : [ApiGoalOwnerType.department, ApiGoalOwnerType.team, ApiGoalOwnerType.employee],
    };
  }, [filter, filterAdditions]);

  const { data: goalsData, isLoading } = useGoalsSearch(filterWithCustom);

  const {
    flatGoals,
    expandedPaths,
    loadingPaths,
    toggleExpand,
    handleEditGoal,
    handleDetailsGoal,
    handleDeleteGoal,
  } = useGoalsTableHook(goalsData?.items ?? []);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((searchParams) => {
        if (newData) searchParams.set("search", newData);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
  });

  const handleCreateGoal = () => {
    modalsService.openModal(GoalEditModal, {});
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.goals.listShown);
  });

  useTitle(t("goals:initiatives_goals"));

  const canCreate =
    canIEdit(ApiPermission.goals_company_manage) ||
    canIEdit(ApiPermission.goals_department_manage_all) ||
    canIEdit(ApiPermission.goals_department_manage_own) ||
    canIEdit(ApiPermission.goals_team_manage_all) ||
    canIEdit(ApiPermission.goals_team_manage_own);

  return (
    <GoalPaneLayout>
      <Page className="h-full">
        <Page.Header title={t("goals:initiatives_goals")}>
          <Stack direction={"horizontal"} gap={"sm"}>
            {renderFilters()}
            {filterButton()}
            {canCreate && (
              <Button variant={"primary"} onClick={handleCreateGoal}>
                {t("goals:create_goal")}
              </Button>
            )}
          </Stack>
        </Page.Header>
        <Page.Content stretch>
          <Page.StickyLayout
            toolbar={
              <Search
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
                clearable={true}
                onClear={clearSearch}
              />
            }
            footer={
              goalsData ? (
                <PaginationBlock
                  metadata={goalsData.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              ) : undefined
            }
          >
            <GoalsTable
              stickyHeader
              flatGoals={flatGoals}
              isLoading={isLoading}
              expandedPaths={expandedPaths}
              loadingPaths={loadingPaths}
              toggleExpand={toggleExpand}
              handleEditGoal={handleEditGoal}
              handleDetailsGoal={handleDetailsGoal}
              handleDeleteGoal={handleDeleteGoal}
              onItemClick={openGoalPanel}
              emptyStateComponent={
                <EmptyStateBase
                  emoji={":dart:"}
                  title={t("goals:goals_empty_state_title")}
                  description={t("goals:goals_empty_state_description")}
                  primaryButton={
                    <Button onClick={handleCreateGoal}>{t("goals:create_goal")}</Button>
                  }
                />
              }
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </GoalPaneLayout>
  );
}
